import React, { useState, useEffect } from "react";
import axios from "axios";
import { Shield } from "lucide-react";
import { getApiErrorMessage } from "../../utils/apiHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BetaGate = ({ onValidCode }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [betaMessage, setBetaMessage] = useState('This application is currently in beta testing. Please enter your invite code to continue.');

  useEffect(() => {
    // Fetch beta message
    const fetchBetaStatus = async () => {
      try {
        const response = await axios.get(`${API}/beta/status`);
        if (response.data.beta_message) {
          setBetaMessage(response.data.beta_message);
        }
      } catch (e) {
        // Use default message
      }
    };
    fetchBetaStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/beta/validate-code`, { code: inviteCode });
      // Store in localStorage so they don't have to re-enter
      localStorage.setItem('beta_access', 'true');
      localStorage.setItem('beta_code_used', inviteCode.toUpperCase());
      onValidCode();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Invalid invite code'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Beta Access</h1>
            <p className="text-white/60 text-sm">{betaMessage}</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Invite Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Enter your invite code"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent uppercase tracking-widest text-center text-lg font-mono"
                maxLength={12}
                required
                data-testid="beta-invite-code-input"
              />
            </div>
            
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-200 text-sm text-center">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !inviteCode.trim()}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              data-testid="beta-submit-btn"
            >
              {loading ? 'Validating...' : 'Enter Beta'}
            </button>
          </form>
          
          <p className="mt-6 text-center text-white/40 text-xs">
            Don't have an invite code? Contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BetaGate;
